import React from 'react';
import {
  Alert,
  Button,
  Descriptions,
  Divider,
  Drawer,
  Image,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { Link } from 'react-router-dom';
import type { MerchantKycDetail } from '../../services/merchantKycService';
import { AppotaPayReferActivationFlow } from '../../components/appotapay/AppotaPayReferActivationFlow';
import { formatCurrencyVND } from '../../utils/transactionHelpers';
import {
  formatVnCalendarDate,
  formatVnDateTimeShort,
} from '../../utils/vnDateTime';

const { Text } = Typography;

const DAY_LABELS = [
  '',
  'Thứ 2',
  'Thứ 3',
  'Thứ 4',
  'Thứ 5',
  'Thứ 6',
  'Thứ 7',
  'Chủ nhật',
];

function boolLabel(value?: boolean) {
  if (value === true) return 'Có';
  if (value === false) return 'Không';
  return '—';
}

function textOrDash(value?: string | number | null) {
  if (value == null || value === '') return '—';
  return String(value);
}

function statusTag(status?: string) {
  switch (status) {
    case 'submitted':
      return <Tag color="processing">Chờ duyệt</Tag>;
    case 'approved':
      return <Tag color="success">Đã duyệt</Tag>;
    case 'rejected':
      return <Tag color="error">Từ chối</Tag>;
    case 'completed':
      return <Tag color="success">Hoàn tất</Tag>;
    case 'in_progress':
      return <Tag color="default">Đang làm</Tag>;
    default:
      return status ? <Tag>{status}</Tag> : '—';
  }
}

function paymentTag(status?: string) {
  if (status === 'paid') return <Tag color="green">Đã thanh toán</Tag>;
  if (status === 'skipped') return <Tag>Miễn phí</Tag>;
  if (status === 'pending') return <Tag color="orange">Chưa thanh toán</Tag>;
  return status ? <Tag>{status}</Tag> : '—';
}

function ImagePreview({ label, url }: { label: string; url?: string | null }) {
  if (!url) {
    return (
      <div>
        <Text type="secondary">{label}</Text>
        <div>—</div>
      </div>
    );
  }
  return (
    <div>
      <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
        {label}
      </Text>
      <Image
        src={url}
        alt={label}
        width={160}
        style={{ borderRadius: 8, objectFit: 'cover' }}
        fallback="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='120'/%3E"
      />
    </div>
  );
}

type Props = {
  open: boolean;
  loading: boolean;
  error: string | null;
  detail: MerchantKycDetail | null;
  onClose: () => void;
};

export function MerchantKycDetailDrawer({
  open,
  loading,
  error,
  detail,
  onClose,
}: Props) {
  const step = detail?.stepData ?? {};
  const region = step.region ?? {};
  const basic = step.basic_info ?? {};
  const rep = step.representative ?? {};
  const bank = step.bank ?? {};
  const photos = step.photos ?? {};
  const hours = step.hours?.days ?? [];
  const payment = step.success_payment ?? {};

  return (
    <Drawer
      title={
        detail
          ? `Chi tiết KYC — ${
              detail.storeName || detail.merchantName || `#${detail.userId}`
            }`
          : 'Chi tiết KYC'
      }
      open={open}
      onClose={onClose}
      width={720}
      destroyOnClose
    >
      {loading && (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      )}

      {!loading && error && (
        <Alert
          type="error"
          message={error}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {!loading && !error && detail && (
        <Space direction="vertical" size={20} style={{ width: '100%' }}>
          <Descriptions
            title="Trạng thái hồ sơ"
            bordered
            size="small"
            column={1}
          >
            <Descriptions.Item label="User ID">
              {detail.userId}
            </Descriptions.Item>
            <Descriptions.Item label="Restaurant ID">
              {detail.restaurantId ? (
                <Link to={`/merchants/${detail.restaurantId}`}>
                  #{detail.restaurantId}
                </Link>
              ) : (
                '—'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Trạng thái KYC">
              {statusTag(detail.status)}
            </Descriptions.Item>
            <Descriptions.Item label="Bước hiện tại">
              {detail.currentStep}/{detail.totalSteps}
            </Descriptions.Item>
            <Descriptions.Item label="Gửi lúc">
              {detail.submittedAt
                ? formatVnDateTimeShort(detail.submittedAt)
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Duyệt lúc">
              {detail.approvedAt
                ? formatVnDateTimeShort(detail.approvedAt)
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Lý do từ chối">
              {textOrDash(detail.rejectionReason)}
            </Descriptions.Item>
            <Descriptions.Item label="Lên sàn">
              {detail.isListed ? (
                <Tag color="success">Đã lên sàn</Tag>
              ) : (
                <Tag>Chưa lên sàn</Tag>
              )}
            </Descriptions.Item>
          </Descriptions>

          {(detail.status === 'approved' || detail.status === 'completed') &&
          detail.restaurantId ? (
            <>
              <Divider orientation="left" plain>
                AppotaPay REFER
              </Divider>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message="KYC đã duyệt — tiếp theo tạo REFER trên portal AppotaPay và nhập credentials"
              />
              <AppotaPayReferActivationFlow status="PENDING_ACTIVATION" />
              <Link to={`/appotapay/merchants/${detail.restaurantId}?link=1`}>
                <Button type="primary" style={{ marginTop: 12 }}>
                  Nhập credentials AppotaPay →
                </Button>
              </Link>
            </>
          ) : null}

          <Divider orientation="left" plain>
            Bước 1–2 · Bắt đầu đăng ký
          </Divider>
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Xác nhận bắt đầu">
              {boolLabel(step.start?.acknowledged)}
            </Descriptions.Item>
            <Descriptions.Item label="Xác nhận tạo quán">
              {boolLabel(step.create_shop?.acknowledged)}
            </Descriptions.Item>
          </Descriptions>

          <Divider orientation="left" plain>
            Bước 3 · Khu vực kinh doanh
          </Divider>
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Tỉnh/Thành">
              {textOrDash(region.provinceName || basic.provinceName)}
            </Descriptions.Item>
            <Descriptions.Item label="Mã tỉnh">
              {textOrDash(region.provinceId || basic.provinceId)}
            </Descriptions.Item>
          </Descriptions>

          <Divider orientation="left" plain>
            Bước 4 · Thông tin cơ bản
          </Divider>
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Tên quán">
              {textOrDash(basic.storeName || detail.storeName)}
            </Descriptions.Item>
            <Descriptions.Item label="SĐT liên hệ">
              {textOrDash(basic.contactPhone || detail.merchantPhone)}
            </Descriptions.Item>
            <Descriptions.Item label="Phường/Xã">
              {textOrDash(basic.wardName)}
            </Descriptions.Item>
            <Descriptions.Item label="Quận/Huyện">
              {textOrDash(basic.districtName)}
            </Descriptions.Item>
            <Descriptions.Item label="Đường/Số nhà">
              {textOrDash(basic.street)}
            </Descriptions.Item>
            <Descriptions.Item label="Địa chỉ đầy đủ">
              {textOrDash(basic.fullAddress)}
            </Descriptions.Item>
            <Descriptions.Item label="Tọa độ">
              {basic.lat != null && basic.lng != null
                ? `${basic.lat}, ${basic.lng}`
                : '—'}
            </Descriptions.Item>
          </Descriptions>

          <Divider orientation="left" plain>
            Bước 5 · Người đại diện
          </Divider>
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Họ tên">
              {textOrDash(rep.fullName || detail.merchantName)}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {textOrDash(rep.email || detail.merchantEmail)}
            </Descriptions.Item>
            <Descriptions.Item label="SĐT">
              {textOrDash(rep.phone || detail.merchantPhone)}
            </Descriptions.Item>
            <Descriptions.Item label="CCCD/CMND">
              {textOrDash(rep.idNumber)}
            </Descriptions.Item>
            <Descriptions.Item label="Ngày cấp">
              {rep.idIssueDate ? formatVnCalendarDate(rep.idIssueDate) : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Nơi cấp">
              {textOrDash(rep.idIssuePlace)}
            </Descriptions.Item>
          </Descriptions>
          <Space size={24} wrap>
            <ImagePreview
              label="Ảnh mặt trước CCCD"
              url={rep.idFrontImagePreviewUrl ?? rep.idFrontImageUrl}
            />
            <ImagePreview
              label="Ảnh mặt sau CCCD"
              url={rep.idBackImagePreviewUrl ?? rep.idBackImageUrl}
            />
          </Space>

          <Divider orientation="left" plain>
            Bước 6 · Ngân hàng
          </Divider>
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Ngân hàng">
              {textOrDash(bank.bankName)}
            </Descriptions.Item>
            <Descriptions.Item label="Mã ngân hàng">
              {textOrDash(bank.bankCode)}
            </Descriptions.Item>
            <Descriptions.Item label="Số tài khoản">
              {textOrDash(bank.accountNumber)}
            </Descriptions.Item>
            <Descriptions.Item label="Chủ tài khoản">
              {textOrDash(bank.accountName)}
            </Descriptions.Item>
            <Descriptions.Item label="Chi nhánh">
              {textOrDash(bank.branch)}
            </Descriptions.Item>
          </Descriptions>

          <Divider orientation="left" plain>
            Bước 7 · Giờ mở cửa
          </Divider>
          {hours.length > 0 ? (
            <Descriptions bordered size="small" column={1}>
              {hours.map((day, index) => {
                const dow = day.dayOfWeek ?? index + 1;
                const label =
                  day.label ||
                  DAY_LABELS[Math.min(Math.max(dow, 1), 7)] ||
                  `Ngày ${dow}`;
                const time =
                  day.enabled === false
                    ? 'Đóng cửa'
                    : `${day.startTime || '—'} – ${day.endTime || '—'}`;
                return (
                  <Descriptions.Item key={`${dow}-${index}`} label={label}>
                    {time}
                  </Descriptions.Item>
                );
              })}
            </Descriptions>
          ) : (
            <Text type="secondary">Chưa có dữ liệu giờ mở cửa</Text>
          )}

          <Divider orientation="left" plain>
            Bước 8 · Ảnh quán
          </Divider>
          <Space size={24} wrap>
            <ImagePreview
              label="Ảnh đại diện"
              url={photos.avatarPreviewUrl ?? photos.avatarUrl}
            />
            <ImagePreview
              label="Ảnh bìa"
              url={photos.coverPreviewUrl ?? photos.coverUrl}
            />
          </Space>

          <Divider orientation="left" plain>
            Bước 9–10 · Gói đăng ký
          </Divider>
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Đã xem giới thiệu gói">
              {boolLabel(step.package_intro?.acknowledged)}
            </Descriptions.Item>
            <Descriptions.Item label="Gói đã chọn">
              {textOrDash(detail.packageName)}
            </Descriptions.Item>
            <Descriptions.Item label="Mã gói">
              {textOrDash(
                step.package_select?.packageId ?? detail.selectedPackageId
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Giá gói">
              {detail.packagePrice != null
                ? formatCurrencyVND(detail.packagePrice)
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Thanh toán gói">
              {paymentTag(detail.packagePaymentStatus)}
            </Descriptions.Item>
            <Descriptions.Item label="Thanh toán lúc">
              {detail.packagePaidAt
                ? formatVnDateTimeShort(detail.packagePaidAt)
                : '—'}
            </Descriptions.Item>
          </Descriptions>

          <Divider orientation="left" plain>
            Bước 11–12 · Hợp đồng & gửi hồ sơ
          </Divider>
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Đồng ý hợp đồng">
              {boolLabel(step.preview_contract?.accepted)}
            </Descriptions.Item>
            <Descriptions.Item label="Phiên bản HĐ">
              {textOrDash(
                step.preview_contract?.contractVersion || detail.contractVersion
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Chấp nhận HĐ lúc">
              {detail.contractAcceptedAt
                ? formatVnDateTimeShort(detail.contractAcceptedAt)
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Đã hoàn tất bước thanh toán">
              {boolLabel(payment.paymentCompleted ?? payment.acknowledged)}
            </Descriptions.Item>
            <Descriptions.Item label="Trạng thái TT (app)">
              {textOrDash(payment.paymentStatus)}
            </Descriptions.Item>
            <Descriptions.Item label="Mã đơn TT">
              {textOrDash(payment.orderId)}
            </Descriptions.Item>
            <Descriptions.Item label="Mã giao dịch">
              {textOrDash(payment.transactionId)}
            </Descriptions.Item>
          </Descriptions>

          {detail.rejectionReason && (
            <Alert
              type="warning"
              showIcon
              message="Lý do từ chối"
              description={detail.rejectionReason}
            />
          )}
        </Space>
      )}

      {!loading && !error && !detail && (
        <Text type="secondary">Không có dữ liệu hồ sơ.</Text>
      )}
    </Drawer>
  );
}
